// =============================================================================
// DEAL STATUS BADGE - Uses centralized styles (CR043)
// =============================================================================

import { cn } from "@/lib/utils";
import { Clock, CheckCircle, XCircle, AlertTriangle, HelpCircle, EyeOff } from "lucide-react";
import { badgeStyles, getBadgeSizeClasses } from "@/lib/deals";
import { getDealTimeStatus } from "@/lib/deals/dealValidity";

export type DealStatus = "active" | "upcoming" | "expired" | "unknown" | "suspended" | "needs_review";

interface DealStatusBadgeProps {
  status: DealStatus;
  size?: "sm" | "md";
  className?: string;
}

/**
 * Computes deal status from dates.
 * CR431: Now delegates to unified dealValidity helper for deal objects,
 * but still supports raw start/end strings for backward compat.
 * 
 * Rule: Missing end_date means deal is Active (detected on current as-of date)
 */
export function computeDealStatus(
  startDateOrDeal: string | null | undefined | Record<string, any>,
  endDate?: string | null | undefined,
  asOfDate?: Date
): DealStatus {
  // CR431: If first arg is a deal-like object, use unified helper
  if (startDateOrDeal && typeof startDateOrDeal === "object") {
    const timeStatus = getDealTimeStatus(startDateOrDeal, asOfDate);
    return timeStatus as DealStatus;
  }

  // Legacy path: raw start/end strings — CR432: delegate to unified helper
  // Build a minimal deal-like object so the helper handles timezone-safe parsing
  const startDate = startDateOrDeal as string | null | undefined;
  if (!startDate && !endDate) {
    return "unknown";
  }
  const syntheticDeal = { start_date: startDate, end_date: endDate };
  return getDealTimeStatus(syntheticDeal, asOfDate) as DealStatus;
}

const statusConfig: Record<DealStatus, {
  label: string;
  icon: React.ElementType;
  className: string;
}> = {
  active: {
    label: "Active",
    icon: CheckCircle,
    className: "bg-green-100 text-green-800 border-green-200",
  },
  upcoming: {
    label: "Upcoming",
    icon: Clock,
    className: "bg-blue-100 text-blue-800 border-blue-200",
  },
  expired: {
    label: "Expired",
    icon: XCircle,
    className: "bg-gray-100 text-gray-600 border-gray-200",
  },
  unknown: {
    label: "Unknown",
    icon: HelpCircle,
    className: "bg-yellow-50 text-yellow-700 border-yellow-200",
  },
  suspended: {
    label: "Suspended",
    icon: EyeOff,
    className: "bg-red-100 text-red-800 border-red-200",
  },
  needs_review: {
    label: "Needs Review",
    icon: AlertTriangle,
    className: "bg-amber-100 text-amber-800 border-amber-200",
  },
};

export function DealStatusBadge({ status, size = "sm", className }: DealStatusBadgeProps) {
  const config = statusConfig[status] || statusConfig.unknown;
  const Icon = config.icon;
  const sizeClasses = getBadgeSizeClasses(size);

  return (
    <span
      className={cn(
        badgeStyles.base,
        sizeClasses.container,
        config.className,
        className
      )}
    >
      <Icon className={sizeClasses.icon} />
      {config.label}
    </span>
  );
}
